import React, { Fragment, useMemo, useState } from 'react'
import { useQueryParams } from 'use-query-params'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Show } from '@app/components/util'
import { LntButton } from '@app/components/base'
import { ContentLayout } from '@app/layouts'
import { ExtendedTable, PermissionWrapper, SubheaderToolbarDivider, TableSettingsToolbar } from '@app/components'
import { ContractorsChangeModal, ScheduleChangeModal } from 'src/pages/MonitoringLaunchPage/ui'
import { ConfirmModal } from '@app/modals'

import { useContractorEditableColumn, useScheduleEditableColumns } from '@app/pages/MonitoringLaunchPage/hooks'

import { createAllTasks, createTasks } from '@app/api/routes/monitoringTasks/monitoringTasks'

import { getSelectedRowsByKeys } from '@app/utils'
import { getMonitoringCreatingTasks, patchCreatingTasks } from '@app/api/routes/monitoringLists'
import { useToggle, useTableSearch, useNotification, usePaginationQueryParams, useTableSetting } from '@app/hooks'

import { BUTTON_TYPE, SIZE } from '@app/constants'
import { MONITORINGS_CREATING_TASKS_KEY } from '@app/constants/queryKeys'
import { PERMISSION } from '@app/constants/permission'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'

import { adaptCreateAllTasksData, adaptCreateMonitoringTask, adaptRegularMonitoringTableData, getColumns } from './lib'
import { TopFilters } from './ui'
import { topFiltersQuerySchema, columnsSearchSchema, tableSearchQuerySchema } from './configs'

import type { Key } from 'react'
import type { ScheduleData } from './types'
import type { FiltersParams } from '@app/types/filter'
import type { GetMonitoringCreatingTasksParams } from '@app/api/routes/monitoringLists/types'

const columns = getColumns()

export const RegularMonitoringTab: React.FC = () => {
  const queryClient = useQueryClient()
  const notification = useNotification()

  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()
  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)

  const groupMonitoringParams = useMemo(
    () => ({ ...paginationParams, ...queryFilters }),
    [paginationParams, queryFilters]
  ) as GetMonitoringCreatingTasksParams

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([])

  const createAllTasksMutation = useMutation({
    mutationFn: createAllTasks,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_CREATING_TASKS_KEY.monitoringsCreatingTasksItems] })

      notification.success({
        message: 'Запуск задач произведен',
        description: 'Запущены все отфильтрованные задачи'
      })
    }
  })

  const { data: regularMonitoringTableData, isLoading: isGroupMonitoringDataLoading } = useQuery({
    queryKey: [MONITORINGS_CREATING_TASKS_KEY.monitoringsCreatingTasksItems, groupMonitoringParams],
    queryFn: async () => await getMonitoringCreatingTasks(groupMonitoringParams),
    select: monitoringCreatingTasksData => adaptRegularMonitoringTableData(monitoringCreatingTasksData)
  })

  const patchCreatingTasksMutation = useMutation(patchCreatingTasks, {
    onSuccess: async () =>
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_CREATING_TASKS_KEY.monitoringsCreatingTasksItems] })
  })

  const scheduleChangeModalToggle = useToggle()
  const contractorsChangeModalToggle = useToggle()
  const monitoringStartAllModalToggle = useToggle()

  const handleScheduleChange = async (scheduleData: ScheduleData): Promise<void> => {
    await patchCreatingTasksMutation.mutateAsync({ runParametersIds: selectedRowKeys, ...scheduleData })

    scheduleChangeModalToggle.setUntoggled()
  }

  const handleContractorsChange = async (contractorId: number): Promise<void> => {
    await patchCreatingTasksMutation.mutateAsync({
      runParametersIds: selectedRowKeys,
      contractorId
    })

    contractorsChangeModalToggle.setUntoggled()
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
  }

  const handleSelectedRowChange = (keys: Key[]): void => {
    setSelectedRowKeys(keys.map(Number))
  }

  const handleMonitoringStartAll = (): void => {
    const mutationData = adaptCreateAllTasksData(queryFilters)
    createAllTasksMutation.mutate(mutationData)
    monitoringStartAllModalToggle.setUntoggled()
  }

  const handleChangeContractorCell = (contractorId: number | null, recordKey: number[]): void => {
    patchCreatingTasksMutation.mutate({ runParametersIds: recordKey, contractorId })
  }

  const columnsWithContractors = useContractorEditableColumn({
    columns,
    patchCreatingTasks: handleChangeContractorCell
  })

  const columnsWithScheduleEditableColumns = useScheduleEditableColumns({
    columns: columnsWithContractors,
    patchCreatingTasks: data => patchCreatingTasksMutation.mutate(data)
  })

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.regularMonitoringTab, columnsWithScheduleEditableColumns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredRegularMonitoringDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: regularMonitoringTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const handleMonitoringStartChecked = async (): Promise<void> => {
    const selectedDataRecords = getSelectedRowsByKeys(selectedRowKeys, filteredRegularMonitoringDataSource)
    const monitoringTasks = adaptCreateMonitoringTask(selectedDataRecords)

    if (!monitoringTasks) return

    await createTasks({ monitoringTasks })

    // TODO Договориться получать с бэка в ответе количество фото? Текст тоже согласовать с Андреем
    notification.success({
      message: 'Запуск задач произведен',
      description: `Запущено задач на мониторинг: ${monitoringTasks.length}`
    })

    setSelectedRowKeys([])
  }

  const toolbar = [
    <Show
      key='date-change_monitorings-turn-on_monitorings-turn-off-show'
      condition={ selectedRowKeys.length > 0 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.startRestartMonitoringTask] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ contractorsChangeModalToggle.setToggled }
        >
          Изменить подрядчика
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.startRestartMonitoringTask] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ scheduleChangeModalToggle.setToggled }
        >
          Изменить даты
        </LntButton>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Show>,

    <Show
      key='show-launch-checked-button'
      condition={ selectedRowKeys.length >= 1 }
    >
      <PermissionWrapper requiredPermissions={ [PERMISSION.startRestartMonitoringTask] }>
        <LntButton
          type={ BUTTON_TYPE.secondary }
          size={ SIZE.small }
          onClick={ handleMonitoringStartChecked }
        >
          Запустить выбранные
        </LntButton>
      </PermissionWrapper>
    </Show>,

    <Fragment key='launch-all-button'>
      <PermissionWrapper requiredPermissions={ [PERMISSION.startRestartMonitoringTask] }>
        <LntButton
          type={ BUTTON_TYPE.primary }
          size={ SIZE.small }
          loading={ createAllTasksMutation.isLoading }
          onClick={ monitoringStartAllModalToggle.setToggled }
        >
          Запустить все
        </LntButton>

        <SubheaderToolbarDivider />
      </PermissionWrapper>
    </Fragment>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <>
      <ContentLayout>
        <TopFilters
          onSearch={ handleFiltersChange }
          initialFilters={ queryFilters as FiltersParams }
        />

        <ExtendedTable
          loading={ isGroupMonitoringDataLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredRegularMonitoringDataSource }
          toolbar={ toolbar }
          rowSelection={ {
            selectedRowKeys,
            onChange: handleSelectedRowChange
          } }
          pagination={ {
            ...pagination,
            total: regularMonitoringTableData?.count,
            onChange: handlePaginationChange
          } }
        />
      </ContentLayout>

      <ScheduleChangeModal
        open={ scheduleChangeModalToggle.value }
        onCancel={ scheduleChangeModalToggle.setUntoggled }
        onScheduleChange={ handleScheduleChange }
      />

      <ContractorsChangeModal
        open={ contractorsChangeModalToggle.value }
        onCancel={ contractorsChangeModalToggle.setUntoggled }
        onContractorsChange={ handleContractorsChange }
      />

      <ConfirmModal
        title='Вы уверены, что хотите произвести массовый запуск задач?'
        cancelText='Отмена'
        open={ monitoringStartAllModalToggle.value }
        onCancel={ monitoringStartAllModalToggle.setUntoggled }
        onConfirm={ handleMonitoringStartAll }
      />
    </>
  )
}
